import React from 'react';
import { Tooltip } from 'antd';
import { CloudOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';

import { TransactionCreativeExpandedCard } from './TransactionCreativeExpandedCard';
import { formatWholeNumber } from '../../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// TransactionCreativeTable — v2 DataTable migration.
//
// V2 data shape: each creative has { name, media, impressions, transactions,
// color }. `media` is either a URL string OR an object with a `key`. The
// expand panel (TransactionCreativeExpandedCard) renders the media preview
// when available.
//
// Structural parity with Display Creatives and Publisher tables — accent
// dot + conditional thumbnail column + name + right-aligned numerics, with
// totals summary and mobile card render.
// =============================================================================

const numericSort = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const nameSort = (rowA, rowB) => {
  const a = rowA.original.name || rowA.original.value || '';
  const b = rowB.original.name || rowB.original.value || '';
  return String(a).localeCompare(String(b));
};

const checkIfExpandable = (record) => {
  if (!record.media || record.media === '') return false;
  const hasMediaUrl = typeof record.media === 'string' && record.media.trim() !== '';
  const hasMediaObject = typeof record.media === 'object' && record.media.key;
  return hasMediaUrl || hasMediaObject;
};

const renderCreativeName = (text) => {
  if (!text) return <div />;
  if (text.length > 45) {
    return (
      <Tooltip overlayClassName="mjl-ds" title={text}>
        <span className="campaign-overview__creatives-name">
          {text.slice(0, 46)}…
        </span>
      </Tooltip>
    );
  }
  return <span className="campaign-overview__creatives-name">{text}</span>;
};

const TransactionCreativeTable = ({ creativeData, showMetrics = false }) => {
  const hasThumbnailColumn =
    !isEmpty(creativeData) &&
    creativeData.some((creative) => checkIfExpandable(creative));

  const columns = [];

  columns.push({
    id: 'color',
    accessorKey: 'color',
    header: '',
    sortable: false,
    meta: { align: 'center', width: 24 },
    cell: ({ getValue }) => (
      <span
        className="campaign-overview__creatives-dot"
        style={{ backgroundColor: getValue() }}
        aria-hidden="true"
      />
    ),
  });

  if (hasThumbnailColumn) {
    columns.push({
      id: 'media',
      accessorKey: 'media',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 60 },
      cell: ({ getValue }) => {
        const media = getValue();
        const url = typeof media === 'string' ? media : null;
        const hasImage = url && url !== '';
        return (
          <div
            className="campaign-overview__creatives-thumb"
            style={hasImage ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!hasImage && <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />}
          </div>
        );
      },
    });
  }

  columns.push({
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    sortable: true,
    sortingFn: nameSort,
    meta: { align: 'left', width: 280 },
    cell: ({ getValue }) => renderCreativeName(getValue()),
  });

  // Impressions & Transactions are only shown for advertisers whose PartnerLevel
  // is allowed to see them (see transactionMetricsVisibility).
  if (showMetrics) {
    columns.push({
      id: 'impressions',
      accessorKey: 'impressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: numericSort('impressions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    });
    columns.push({
      id: 'transactions',
      accessorKey: 'transactions',
      header: 'Transactions',
      sortable: true,
      sortingFn: numericSort('transactions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    });
  }

  const totalImpressions = (creativeData || []).reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalTransactions = (creativeData || []).reduce(
    (sum, d) => sum + (parseFloat(d.transactions) || 0),
    0
  );

  const renderMobileCard = (record) => {
    const media = record.media;
    const url = typeof media === 'string' ? media : null;
    const hasImage = url && url !== '';
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <div
            className="campaign-overview__creatives-thumb"
            style={hasImage ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!hasImage && <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />}
          </div>
          <div className="campaign-overview__creatives-card-name">
            <span
              className="campaign-overview__creatives-dot"
              style={{ backgroundColor: record.color }}
              aria-hidden="true"
            />
            {renderCreativeName(record.name || record.value)}
          </div>
        </div>
        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.impressions ? formatWholeNumber(record.impressions) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Transactions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.transactions ? formatWholeNumber(record.transactions) : '-'}
            </span>
          </div>
        </div>
      </>
    );
  };

  return (
    <DataTable
      data={creativeData || []}
      columns={columns}
      pageSize={10}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      expandable={{
        isRowExpandable: (record) => checkIfExpandable(record),
        renderExpandedRow: (record) =>
          record && <TransactionCreativeExpandedCard record={record} />,
      }}
      emptyState="No creatives yet."
    />
  );
};

export default TransactionCreativeTable;
